import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Grid,
  Group,
  ThemeIcon,
  Table,
  Loader,
  Button,
  Select,
  Badge,
  Pagination,
  ScrollArea,
} from "@mantine/core";
import {
  IconTrendingUp,
  IconCalendar,
  IconDownload,
  IconRefresh,
  IconCurrencyDollar,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { usePerformRequest } from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { DatePickerInput } from "@mantine/dates";

interface DisbursementSummary {
  totalDisbursements: number;
  totalDisbursedAmount: number;
  averageDisbursement: number;
}

interface DisbursementByProduct {
  _id: string;
  count: number;
  totalAmount: number;
}

interface DisbursementByDay {
  _id: string;
  count: number;
  totalAmount: number;
}

interface DisbursementResponse {
  summary: DisbursementSummary;
  disbursementsByProduct: DisbursementByProduct[];
  disbursementsByDay: DisbursementByDay[];
}

interface DisbursementItem {
  _id: string;
  userID: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email: string;
    phone: string;
    bankAccount?: {
      accountNumber: string;
      accountName: string;
      bankName?: string;
    };
  };
  loanRequestID?: {
    _id: string;
    loanID?: {
      name: string;
      interestRate?: number;
    };
  };
  amount: number;
  status: "completed" | "pending" | "failed";
  method: string;
  paystackReference?: string;
  paystackTransactionId?: number;
  description: string;
  metadata?: {
    failureReason?: string;
    transferStatus?: string;
    bankAccount?: any;
  };
  createdAt: number;
  completedAt?: number;
}

interface DisbursementListResponse {
  data: DisbursementItem[];
  count: number;
  totalRecords: number;
  page: number;
  limit: number;
}

export default function DisbursementReport() {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [status, setStatus] = useState<"completed" | "pending" | "failed" | "">("");
  const [dateRange, setDateRange] = useState<{ startDate?: number; endDate?: number; status?: "completed" | "pending" | "failed" }>({});
  const [listPage, setListPage] = useState(1);
  const listLimit = 20;

  const {
    data: reportData,
    isLoading,
    reloadData,
  } = usePerformRequest<{ data: DisbursementResponse }>({
    method: Endpoints.GetDisbursementReport(
      dateRange.startDate,
      dateRange.endDate,
      dateRange.status
    ).method,
    route: Endpoints.GetDisbursementReport(
      dateRange.startDate,
      dateRange.endDate,
      dateRange.status
    ).route,
  });

  useEffect(() => {
    const getTimestamp = (date: Date | null | string | unknown): number | undefined => {
      if (!date) return undefined;
      if (date instanceof Date) return date.getTime();
      if (typeof date === 'string') {
        const parsed = new Date(date);
        return isNaN(parsed.getTime()) ? undefined : parsed.getTime();
      }
      return undefined;
    };
    const start = getTimestamp(startDate);
    const end = getTimestamp(endDate);
    setDateRange({ 
      startDate: start, 
      endDate: end,
      status: status || undefined,
    });
  }, [startDate, endDate, status]);

  const {
    data: listData,
    isLoading: isListLoading,
    reloadData: reloadList,
  } = usePerformRequest<{ data: DisbursementListResponse }>({
    method: Endpoints.GetDisbursementList(
      listPage,
      listLimit,
      dateRange.startDate,
      dateRange.endDate,
      dateRange.status
    ).method,
    route: Endpoints.GetDisbursementList(
      listPage,
      listLimit,
      dateRange.startDate,
      dateRange.endDate,
      dateRange.status
    ).route,
  });

  useEffect(() => {
    reloadData();
    reloadList();
  }, [dateRange]);

  useEffect(() => {
    reloadList();
  }, [listPage]);

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const summary = reportData?.data?.summary;
  const byProduct = reportData?.data?.disbursementsByProduct || [];
  const byDay = reportData?.data?.disbursementsByDay || [];

  const statCards = summary
    ? [
        {
          label: "Total Disbursements",
          value: summary.totalDisbursements.toLocaleString(),
          color: "#699269",
        },
        {
          label: "Total Disbursed",
          value: formatCurrency(summary.totalDisbursedAmount),
          color: "#558B2F",
        },
        {
          label: "Average Disbursement",
          value: formatCurrency(summary.averageDisbursement),
          color: "#33691E",
        },
      ]
    : [];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Group gap="md">
            <ThemeIcon size={48} radius="md" style={{ background: "#699269" }}>
              <IconTrendingUp size={24} color="white" />
            </ThemeIcon>
            <div>
              <Title order={2} fw={700}>
                Disbursement Report
              </Title>
              <Text size="sm" c="dimmed">
                Track all loan disbursements and trends
              </Text>
            </div>
          </Group>
          <Group gap="sm">
            <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => reloadData()}>
              Refresh
            </Button>
            <Button variant="light" leftSection={<IconDownload size={16} />} disabled>
              Export
            </Button>
          </Group>
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group gap="md">
            <DatePickerInput
              label="Start Date"
              placeholder="Select start date"
              value={startDate}
              onChange={(value) => setStartDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
            />
            <DatePickerInput
              label="End Date"
              placeholder="Select end date"
              value={endDate}
              onChange={(value) => setEndDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
              minDate={
                startDate
                  ? startDate instanceof Date
                    ? new Date(startDate.getTime() + 86400000)
                    : new Date(new Date(startDate as string).getTime() + 86400000)
                  : undefined
              }
            />
            <Select
              label="Status"
              placeholder="All statuses"
              value={status || null}
              onChange={(value) => setStatus((value as "completed" | "pending" | "failed" | "") || "")}
              data={[
                { value: "", label: "All Statuses" },
                { value: "completed", label: "Completed" },
                { value: "pending", label: "Pending" },
                { value: "failed", label: "Failed" },
              ]}
              clearable
              style={{ flex: 1 }}
            />
          </Group>
        </Paper>

        {isLoading ? (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Group justify="center">
              <Loader size="lg" />
            </Group>
          </Paper>
        ) : summary ? (
          <>
            <Grid gutter="md">
              {statCards.map((card, index) => (
                <Grid.Col key={card.label} span={{ base: 12, sm: 6, md: 4 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Paper shadow="sm" p="lg" radius="md" withBorder style={{ height: "100%" }}>
                      <Stack gap="xs">
                        <Text size="sm" c="dimmed" fw={500}>
                          {card.label}
                        </Text>
                        <Text size="xl" fw={700} style={{ color: card.color }}>
                          {card.value}
                        </Text>
                      </Stack>
                    </Paper>
                  </motion.div>
                </Grid.Col>
              ))}
            </Grid>

            {byProduct.length > 0 && (
              <Paper shadow="sm" p="lg" radius="md" withBorder>
                <Title order={4} mb="md">
                  Disbursements by Product
                </Title>
                <Table.ScrollContainer minWidth={300}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Product</Table.Th>
                        <Table.Th>Count</Table.Th>
                        <Table.Th style={{ textAlign: "right" }}>Total Amount</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {byProduct.map((item, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>
                            <Text fw={500}>{item._id || "Unknown"}</Text>
                          </Table.Td>
                          <Table.Td>{item.count}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600}>{formatCurrency(item.totalAmount)}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Paper>
            )}

            {byDay.length > 0 && (
              <Paper shadow="sm" p="lg" radius="md" withBorder>
                <Title order={4} mb="md">
                  Daily Disbursements
                </Title>
                <Table.ScrollContainer minWidth={300}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Date</Table.Th>
                        <Table.Th>Count</Table.Th>
                        <Table.Th style={{ textAlign: "right" }}>Amount</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {byDay.map((item, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>
                            <Text fw={500}>{new Date(item._id).toLocaleDateString()}</Text>
                          </Table.Td>
                          <Table.Td>{item.count}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600}>{formatCurrency(item.totalAmount)}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Paper>
            )}

            {/* Detailed Disbursements Table */}
            <Paper shadow="sm" p="lg" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Title order={4}>Disbursements List</Title>
                <Text size="sm" c="dimmed">
                  {listData?.data?.totalRecords || 0} total disbursements
                </Text>
              </Group>
              {isListLoading ? (
                <Group justify="center" py="xl">
                  <Loader size="lg" />
                </Group>
              ) : listData?.data?.data && listData.data.data.length > 0 ? (
                <>
                  <ScrollArea>
                    <Table.ScrollContainer minWidth={1000}>
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Date/Time</Table.Th>
                            <Table.Th>User</Table.Th>
                            <Table.Th>Loan Product</Table.Th>
                            <Table.Th style={{ textAlign: "right" }}>Amount</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Bank Account</Table.Th>
                            <Table.Th>Reference</Table.Th>
                            <Table.Th>Notes</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {listData.data.data.map((item) => (
                            <Table.Tr key={item._id}>
                              <Table.Td>
                                <Stack gap={2}>
                                  <Text size="sm" fw={500}>
                                    {new Date(item.createdAt).toLocaleDateString()}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    {new Date(item.createdAt).toLocaleTimeString()}
                                  </Text>
                                </Stack>
                              </Table.Td>
                              <Table.Td>
                                <Stack gap={2}>
                                  <Text size="sm" fw={500}>
                                    {item.userID?.firstName} {item.userID?.lastName}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    {item.userID?.email}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    {item.userID?.phone}
                                  </Text>
                                </Stack>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm" fw={500}>
                                  {item.loanRequestID?.loanID?.name || "N/A"}
                                </Text>
                              </Table.Td>
                              <Table.Td style={{ textAlign: "right" }}>
                                <Text size="sm" fw={600}>
                                  {formatCurrency(item.amount)}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  color={
                                    item.status === "completed"
                                      ? "green"
                                      : item.status === "pending"
                                      ? "yellow"
                                      : "red"
                                  }
                                  variant="light"
                                >
                                  {item.status.toUpperCase()}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                {item.userID?.bankAccount ? (
                                  <Stack gap={2}>
                                    <Text size="xs" fw={500}>
                                      {item.userID.bankAccount.accountName}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {item.userID.bankAccount.accountNumber}
                                    </Text>
                                    {item.userID.bankAccount.bankName && (
                                      <Text size="xs" c="dimmed">
                                        {item.userID.bankAccount.bankName}
                                      </Text>
                                    )}
                                  </Stack>
                                ) : (
                                  <Text size="xs" c="dimmed">
                                    N/A
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                {item.paystackReference ? (
                                  <Text size="xs" style={{ fontFamily: "monospace" }}>
                                    {item.paystackReference}
                                  </Text>
                                ) : (
                                  <Text size="xs" c="dimmed">
                                    -
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                {item.status === "failed" && item.metadata?.failureReason ? (
                                  <Text size="xs" c="red">
                                    {item.metadata.failureReason}
                                  </Text>
                                ) : (
                                  <Text size="xs" c="dimmed">
                                    {item.description}
                                  </Text>
                                )}
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  </ScrollArea>
                  {listData.data.totalRecords > listLimit && (
                    <Group justify="center" mt="md">
                      <Pagination
                        value={listPage}
                        onChange={setListPage}
                        total={Math.ceil(listData.data.totalRecords / listLimit)}
                        size="sm"
                      />
                    </Group>
                  )}
                </>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  No disbursements found
                </Text>
              )}
            </Paper>
          </>
        ) : (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Text c="dimmed" ta="center">
              No data available
            </Text>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

